import React, { useMemo, useState } from 'react';
import {
  Download,
  FileArchive,
  FileImage,
  FileSpreadsheet,
  FileText,
  File as FileIcon,
  ExternalLink,
} from 'lucide-react';
import { Modal } from '@components/molecules/Modal';
import { Document } from '@models/document.model';
import { cn } from '@utils/cn';
import { useTranslation } from '@hooks/useTranslation';

interface BudgetDocumentsModalProps {
  isOpen: boolean;
  onClose: () => void;
  documents: Document[];
  /** Shown under the title so the user knows which bid these belong to. */
  bidName?: string;
}

type FileKind = 'image' | 'pdf' | 'sheet' | 'doc' | 'archive' | 'other';

const EXTENSION_KINDS: { kind: FileKind; pattern: RegExp }[] = [
  { kind: 'image', pattern: /\.(jpe?g|png|gif|bmp|webp|heic|svg)$/i },
  { kind: 'pdf', pattern: /\.pdf$/i },
  { kind: 'sheet', pattern: /\.(xlsx?|csv|numbers)$/i },
  { kind: 'doc', pattern: /\.(docx?|rtf|txt|pages)$/i },
  { kind: 'archive', pattern: /\.(zip|rar|7z|tar|gz)$/i },
];

const kindOf = (name: string): FileKind =>
  EXTENSION_KINDS.find(({ pattern }) => pattern.test(name))?.kind ?? 'other';

/** Each kind gets its own icon and hue, so a folder of files is scannable by shape. */
const KIND_STYLE: Record<FileKind, { Icon: typeof FileIcon; tint: string }> = {
  image: { Icon: FileImage, tint: 'text-violet-600 bg-violet-50' },
  pdf: { Icon: FileText, tint: 'text-red-600 bg-red-50' },
  sheet: { Icon: FileSpreadsheet, tint: 'text-emerald-600 bg-emerald-50' },
  doc: { Icon: FileText, tint: 'text-blue-600 bg-blue-50' },
  archive: { Icon: FileArchive, tint: 'text-amber-600 bg-amber-50' },
  other: { Icon: FileIcon, tint: 'text-gray-500 bg-gray-100' },
};

// Sprint 31, story-158 (house style §13.2): `count === 1`, not `count > 1` — zero is plural in
// Spanish (this story's named zero-handling bug, fixed here while converting).
type FileKindEnumLabels = ReturnType<typeof useTranslation<'enumLabels'>>;
function fileKindLabel(t: FileKindEnumLabels, kind: FileKind, count: number): string {
  switch (kind) {
    case 'image':
      return count === 1 ? t.fileKindImageOne : t.fileKindImageOther;
    case 'pdf':
      return count === 1 ? t.fileKindPdfOne : t.fileKindPdfOther;
    case 'sheet':
      return count === 1 ? t.fileKindSheetOne : t.fileKindSheetOther;
    case 'doc':
      return count === 1 ? t.fileKindDocOne : t.fileKindDocOther;
    case 'archive':
      return count === 1 ? t.fileKindArchiveOne : t.fileKindArchiveOther;
    default:
      return count === 1 ? t.fileKindOtherOne : t.fileKindOtherOther;
  }
}

const extensionOf = (name: string): string => {
  const match = /\.([a-z0-9]+)$/i.exec(name);
  return match ? match[1].toUpperCase() : '';
};

/**
 * One attachment. Images get a real thumbnail — for a site photo the picture *is*
 * the filename, and "IMG_4821.jpg" tells nobody anything.
 */
const DocumentRow: React.FC<{ document: Document }> = ({ document }) => {
  const tEnumLabels = useTranslation('enumLabels');
  const kind = kindOf(document.name);
  const { Icon, tint } = KIND_STYLE[kind];
  const label = fileKindLabel(tEnumLabels, kind, 1);
  const [thumbnailFailed, setThumbnailFailed] = useState(false);
  const showThumbnail = kind === 'image' && Boolean(document.url) && !thumbnailFailed;

  return (
    <a
      href={document.url}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        'group flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-3',
        'hover:border-indigo-300 hover:shadow-sm transition-all',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
        'dark:bg-gray-800 dark:border-gray-700 dark:hover:border-indigo-500',
      )}>
      <div
        className={cn(
          'flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-md',
          // A near-white photo would otherwise read as an empty box, so the
          // thumbnail keeps a hairline ring to prove something is there.
          showThumbnail
            ? 'bg-gray-100 ring-1 ring-inset ring-gray-300 dark:ring-gray-600'
            : tint,
        )}>
        {showThumbnail ? (
          <img
            src={document.url}
            alt=""
            loading="lazy"
            onError={() => setThumbnailFailed(true)}
            className="h-full w-full object-cover"
          />
        ) : (
          <Icon size={20} aria-hidden />
        )}
      </div>

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
          {document.name}
        </p>
        <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
          {extensionOf(document.name) || label}
          {document.category ? ` · ${document.category}` : ''}
        </p>
      </div>

      <span
        className={cn(
          'flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium',
          'text-indigo-600 opacity-0 transition-opacity group-hover:opacity-100 group-focus:opacity-100',
        )}>
        Open
        <ExternalLink size={13} aria-hidden />
      </span>
    </a>
  );
};

/**
 * The attachments on a bid, opened from the paperclip in its row.
 *
 * Read-only on purpose: the table is a place to triage, and uploading or
 * deleting belongs in the bid itself where there is context for it.
 */
export const BudgetDocumentsModal: React.FC<BudgetDocumentsModalProps> = ({
  isOpen,
  onClose,
  documents,
  bidName,
}) => {
  const tEnumLabels = useTranslation('enumLabels');
  // Group by kind so ten site photos and one contract do not interleave.
  const grouped = useMemo(() => {
    const order: FileKind[] = ['pdf', 'doc', 'sheet', 'image', 'archive', 'other'];
    const buckets = new Map<FileKind, Document[]>();
    documents.forEach((document) => {
      const kind = kindOf(document.name);
      buckets.set(kind, [...(buckets.get(kind) ?? []), document]);
    });
    return order
      .filter((kind) => buckets.has(kind))
      .map((kind) => ({ kind, items: buckets.get(kind) as Document[] }));
  }, [documents]);

  const openAll = () => {
    documents.forEach((document) => window.open(document.url, '_blank', 'noopener'));
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="medium"
      title="Attachments"
      description={bidName}
      actionButtons={
        documents.length > 1 ? (
          <button
            type="button"
            onClick={openAll}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium',
              'text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/30',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
            )}>
            <Download size={15} aria-hidden />
            Open all {documents.length}
          </button>
        ) : undefined
      }>
      {documents.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-10 text-center">
          <FileIcon size={30} className="text-gray-300" aria-hidden />
          <p className="text-sm text-gray-500 dark:text-gray-400">
            This bid has no attachments.
          </p>
        </div>
      ) : (
        <div className="flex max-h-[60vh] flex-col gap-4 overflow-y-auto pr-1">
          {grouped.map(({ kind, items }) => (
            <section key={kind} className="flex flex-col gap-2">
              {grouped.length > 1 && (
                <h3 className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                  {fileKindLabel(tEnumLabels, kind, items.length)} · {items.length}
                </h3>
              )}
              {items.map((document, index) => (
                <DocumentRow
                  key={document.documentId ?? `${document.name}-${index}`}
                  document={document}
                />
              ))}
            </section>
          ))}
        </div>
      )}
    </Modal>
  );
};

export default BudgetDocumentsModal;
